import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {Form, FormProvider, useForm} from "react-hook-form";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Utils
import {getAncestryName} from "./helpers.js";

// Components
import Modal from "../../components/Modal.js";
import InputSelect from "../../components/form/InputSelect.js";

// Style
import {pad} from "../../style/components/variables.js";
import {Button, Heading, Inline, Label, Span, Text} from "../../style/components/general.js";
import InputError from "../../components/form/InputError.js";

const schema = yup.object().shape({
  updates: yup
    .mixed()
    .nullable()
    .test({
      test: val =>
        Array.isArray(val) &&
        val.every(mappings => Object.values(mappings).every(mapping => !!mapping)),
      message: "Please provide a selection for each condition that needs to be updated."
    })
});

const checkMap = {
  "===": "is",
  "!==": "is not"
};

const ModalCheckCondition = ({
  visible,
  setVisible,
  builder,
  target,
  original,
  affectedFields,
  handleSave
}) => {
  const {allIds, byId} = builder;

  const form = useForm({
    resolver: yupResolver(schema)
  });
  const {
    handleSubmit,
    formState: {errors}
  } = form;

  const compareMap = useMemo(() => {
    const {options: oOptions, on: oOn, off: oOff} = original;
    const {options: uOptions, on: uOn, off: uOff} = target;

    const map = {
      [oOn]: uOn,
      [oOff]: uOff
    };

    const len = Math.min(oOptions?.length || 0, uOptions?.length || 0);
    for (let i = 0; i < len; i++) {
      map[oOptions[i].option] = uOptions[i].option;
    }

    return map;
  }, [original, target]);

  const formatConditionDescription = ({action, list}) => {
    const targetConditions = [];

    list.map(({depends, check, compare}) => {
      const checkArray = Array.isArray(check) ? check : [check];
      const compareArray = Array.isArray(compare) ? compare : [compare];
      const dependsLabel = byId[depends]?.label || depends?.toUpperCase();

      if (depends === target.name) {
        compareArray.map((val, i) => {
          if (val !== compareMap[val]) {
            const checkFormatted =
              checkArray[i] in checkMap ? checkMap[checkArray[i]] : checkArray[i];
            targetConditions.push({
              depends,
              prefix: `${action} when ${dependsLabel} ${checkFormatted}`,
              compare: val
            });
          }
        });
      }
    });

    return targetConditions;
  };

  const updatedBuilder = ({updates}) => {
    const updated = {...byId};
    updates.map(pair => {
      const [key, value] = Object.entries(pair)[0];
      const {condition} = byId[key];
      const updatedCondition = {
        ...condition,
        list: condition.list.map(item =>
          target.name === item.depends ? {...item, compare: value} : item
        )
      };
      updated[key] = {
        ...updated[key],
        condition: updatedCondition
      };
    });
    handleSave({allIds, byId: updated});
  };

  return (
    <Modal visible={visible} setVisible={setVisible} goBack={() => setVisible(false)} hasBackButton>
      <Heading>Update Affected Conditions</Heading>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(updatedBuilder)} noValidate>
          {affectedFields?.map(({name, condition}, i) => {
            const conditions = formatConditionDescription(condition);
            return (
              <Row key={name}>
                <Label bold>{getAncestryName(byId[name], builder)}</Label>
                {conditions.map(({prefix, compare}) => (
                  <Inline key={condition} fullWidth>
                    <Text>{prefix}</Text>
                    <Span strike>{compare}</Span>
                    <div>
                      <InputSelect
                        name={`updates[${i}].${name}`}
                        placeholder="New target..."
                        options={
                          ["multiselect", "radio", "dropdown"].includes(target.type)
                            ? target.options
                            : [target.off, target.on]
                        }
                        fullWidth={false}
                      />
                    </div>
                  </Inline>
                ))}
              </Row>
            );
          })}
          <InputError name="updates" errors={errors} atBottom={false} />
          <Button type="submit">Save</Button>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalCheckCondition.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  target: PropTypes.objectOf(PropTypes.any).isRequired,
  original: PropTypes.objectOf(PropTypes.any).isRequired,
  affectedFields: PropTypes.arrayOf(PropTypes.any).isRequired,
  handleSave: PropTypes.func.isRequired
};

// Style Overrides
const Row = styled.div`
  margin-top: ${pad}px;
`;

export default ModalCheckCondition;
